<template>
  <div id="pages-hierarchy" class="pages-hierarchy">
    <div class="list" :class="{ flat: hasNoHierarchy }">
      <div v-if="displayed.length === 0" class="empty">
        <span>Create a page, give it a title and start taking notes.</span>
        <BaseButton
          v-if="writable"
          kind="main"
          type="secondary"
          text="New"
          @click="addPageTo()"
        >
          <template #icon>
            <AddIcon />
          </template>
        </BaseButton>
      </div>
      <PagesList
        v-else
        :display-children="true"
        :writable="writable"
        :collapsed-folders="collapsedFolders"
        :pages="displayed"
        :opened-page-id="null"
        :selected-page-id="selectedPageId"
        @add-page-to="addPageTo"
        @open-page="openPageFullScreen"
        @move-page="onMovePage"
        @reorder-page="onReorderPage"
        @select-page="selectPage"
        @show-context-menu="onShowContextMenu"
        @reset-open-folders="resetOpenFolders"
        @toggle-folder="toggleFolder"
      />
    </div>
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch,
  type PropType,
} from "vue";
import { useRoute } from "vue-router";

import BaseButton from "@/components/base/BaseButton.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import * as inject from "@/components/inject";
import {
  getHierarchy,
  recordPageOrder,
  type PageOrder,
} from "@/components/pageHierarchy";
import type { PagesForestSource } from "@/components/pages/pagesForest";
import PagesList from "@/components/pages/PagesList.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useFolders } from "@/composables/usePages";
import { useScrollable } from "@/composables/useScrollable";
import {
  findNodeInForest,
  flattenPagesHierarchies,
  type PagesForestNode,
} from "@/page";
import { useReleaser } from "@/ref";

/**
 * The pages forest pane, shared by the personal and team Pages screens: the
 * nested drag & drop lists (Escape cancels a drag), manual sibling ordering
 * and collapse state (device-local), selection — single click previews in
 * the right pane, double click opens full screen — and reselecting a
 * neighbor when the selected node disappears. Everything data-model
 * specific (forest maintenance, persistence, navigation, context menus)
 * lives in the `source` (see `PagesForestSource`).
 */
export default defineComponent({
  name: "PagesForestPane",
  components: { AddIcon, BaseButton, PagesList },
  props: {
    source: { type: Object as PropType<PagesForestSource>, required: true },
    // Whether the pages can be written: hides the empty-state "New" button
    // and the rows' hover "+" (subpage creation), and disables dragging
    // (re-parenting writes the page's parent). The team screen turns it off
    // for workspaces the user can only read.
    writable: { type: Boolean, default: true },
  },
  setup(props) {
    // The source is bound for the pane's lifetime: the owners re-key the
    // pane when they swap sources (e.g. the team screen per workspace).
    const source = props.source;
    const profile = inject.profile();
    const logger = inject.logger("PagesForestPane");
    const route = useRoute();
    const releaser = useReleaser();

    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const pageOrder = releaser.add(
      syncState<PageOrder>(source.orderKey, { root: [] }, { deep: true })
    );
    const {
      collapsedFolders,
      resetOpenFolders,
      toggleFolder,
      release: releaseFolders,
    } = useFolders(profile, source.collapsedKey);
    releaser.defer(releaseFolders);

    // Register the pane as a scrollable container.
    const scrollable = useScrollable(["pages-hierarchy"]);
    onMounted(scrollable.mount);
    releaser.defer(scrollable.unmount);

    // What the lists render: a disposable copy of the source's forest with
    // the pane's manual sibling order applied (see `getHierarchy` — drags
    // mutate the copy optimistically, never the canonical forest).
    // Re-derived on the source's explicit `changed` tick, not a deep
    // watch, so mid-drag optimistic mutations don't trigger re-derivations
    // that would yank the dragged node.
    const displayed = ref<PagesForestNode[]>([]);
    const reorder = () =>
      getHierarchy(source.forest.value, displayed, pageOrder);
    reorder();

    // The last-clicked node (persistent highlight on its item): a single
    // click selects and previews it in the right pane; a double click opens
    // it full screen.
    const selectedPageId = ref<string | null>(null);
    const selectPage = (
      id: string,
      kind: PagesForestNode["kind"],
      event?: MouseEvent
    ) => {
      selectedPageId.value = id;
      return source.preview({ id, kind }, event);
    };
    const openPageFullScreen = (
      id: string,
      kind: PagesForestNode["kind"],
      event?: MouseEvent
    ) => {
      selectedPageId.value = id;
      return source.openFullScreen({ id, kind }, event);
    };

    // A forest change: re-apply the manual order and, when the selected
    // node vanished (deleted page, object host emptied of its pages),
    // re-seat the selection on the next surviving node below it in the
    // pane's order — or the closest one above when it was last —
    // previewing it in the right pane (or clearing the preview when
    // nothing is left).
    releaser.defer(
      watch(source.changed, () => {
        const before = flattenPagesHierarchies(displayed.value);
        reorder();
        const selected = selectedPageId.value;
        if (selected === null || !before.includes(selected)) return;
        const surviving = new Set(flattenPagesHierarchies(displayed.value));
        if (surviving.has(selected)) return;
        const index = before.indexOf(selected);
        const fallback =
          before.slice(index + 1).find((id) => surviving.has(id)) ??
          before
            .slice(0, index)
            .reverse()
            .find((id) => surviving.has(id)) ??
          null;
        selectedPageId.value = fallback;
        const node =
          fallback === null
            ? null
            : findNodeInForest(displayed.value, fallback);
        void source.preview(
          node === null ? null : { id: node.id, kind: node.kind }
        );
      })
    );

    // Keep the previewed node visible: expand the collapsed ancestors of
    // the node the route's `id` query names (both Pages screens preview
    // through it) — e.g. a deep link, or a subtree collapsed on another
    // device.
    const ancestorsOf = (id: string): string[] => {
      const chain: string[] = [];
      const visit = (nodes: PagesForestNode[]): boolean => {
        for (const node of nodes) {
          if (node.id === id) return true;
          chain.push(node.id);
          if (visit(node.children)) return true;
          chain.pop();
        }
        return false;
      };
      return visit(displayed.value) ? chain : [];
    };
    releaser.defer(
      watch(
        [() => route.query.id, source.changed] as const,
        ([id]) => {
          if (typeof id !== "string") return;
          const ancestors = ancestorsOf(id);
          if (!ancestors.some((a) => collapsedFolders.value.includes(a)))
            return;
          collapsedFolders.value = collapsedFolders.value.filter(
            (a) => !ancestors.includes(a)
          );
        },
        { immediate: true }
      )
    );

    const addPageTo = (parentId?: string) => {
      source.createPage(parentId ?? null).catch((error: unknown) => {
        logger.error("creating a page failed", { parentId, error });
      });
    };

    // A drag landed the page under `parentId` at sibling index `newIndex`:
    // record it in the device-local sibling order. The lists only report
    // intents (their props are readonly); the pane owns the writable
    // order ref.
    const onReorderPage = (
      id: string,
      parentId: string | null,
      newIndex: number
    ) => {
      pageOrder.value = recordPageOrder(
        pageOrder.value,
        id,
        parentId,
        newIndex
      );
    };

    const onMovePage = (
      id: string,
      parentId: string | null,
      newIndex: number
    ) => {
      onReorderPage(id, parentId, newIndex);
      source.movePage(id, parentId).catch((error: unknown) => {
        logger.error("persisting a drag move failed", { id, parentId, error });
      });
    };

    const onShowContextMenu = (
      id: string,
      kind: PagesForestNode["kind"],
      event: MouseEvent
    ) => {
      void source.showContextMenu({ id, kind }, event);
    };

    const hasNoHierarchy = computed(() =>
      displayed.value.every((node) => node.children.length === 0)
    );

    return {
      addPageTo,
      collapsedFolders,
      displayed,
      hasNoHierarchy,
      onMovePage,
      onReorderPage,
      onShowContextMenu,
      openPageFullScreen,
      resetOpenFolders,
      selectPage,
      selectedPageId,
      toggleFolder,
    };
  },
});
</script>
<style scoped lang="scss">
.pages-hierarchy {
  height: 100%;
  .list {
    height: 100%;
    .nested {
      padding-bottom: 16px;
    }
    .empty {
      padding: 0 16px;
      display: flex;
      flex-direction: column;
      row-gap: 16px;
      align-items: center;
      justify-content: center;
      height: calc(100% - 64px);
      span {
        text-align: center;
        @extend %font-caption-regular;
        @include themed() {
          color: t($grey-50);
        }
      }
      button.base-button {
        margin: 0;
      }
    }
  }
}
</style>
